import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, DollarSign, Cpu, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const PERIODS = [
  { label: '7 Days', days: 7 },
  { label: '2 Weeks', days: 14 },
  { label: '4 Weeks', days: 28 },
  { label: '3 Months', days: 90 },
  { label: 'All Time', days: null },
];

const INPUT_COST_PER_TOKEN = 0.25 / 1_000_000;
const OUTPUT_COST_PER_TOKEN = 2.0 / 1_000_000;
const CACHE_KEY = 'dev_stats_cache';
const CACHE_TTL = 120000; // 2 min

const DeveloperStatistics = () => {
  const { organizationId } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === 'dark';
  const [period, setPeriod] = useState(28);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tablePage, setTablePage] = useState(0);
  const tablePerPage = 10;

  useEffect(() => {
    if (!organizationId) return;
    loadStats();
  }, [organizationId, period]);

  const loadStats = async () => {
    // Check sessionStorage cache
    const cacheKey = `${CACHE_KEY}_${organizationId}_${period}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        const { data: d, ts } = JSON.parse(cached);
        if (Date.now() - ts < CACHE_TTL) { setData(d); setLoading(false); return; }
      } catch {}
    }

    setLoading(true);
    try {
      let q = supabase
        .from('sessions')
        .select('id, lesson_timestamp, processed_at, tutor_name, student_name, raw_transcript, prompt_tokens, completion_tokens, total_tokens, api_cost')
        .eq('organization_id', organizationId)
        .eq('is_processed_v2', true)
        .order('lesson_timestamp', { ascending: false });

      if (period) {
        const since = new Date(Date.now() - period * 86400000).toISOString();
        q = q.gte('lesson_timestamp', since);
      }
      q = q.limit(2000);

      const { data: sessions } = await q;
      const sess = sessions || [];

      let totalInputTokens = 0, totalOutputTokens = 0, totalCost = 0;
      let totalInputChars = 0, totalOutputChars = 0;
      const dailyMap = {};
      const perLesson = [];

      for (const s of sess) {
        const hasActual = s.prompt_tokens > 0;
        const inputChars = s.raw_transcript ? s.raw_transcript.length : 0;
        const inputTokens = s.prompt_tokens || Math.round(inputChars / 4);
        const outputTokens = s.completion_tokens || Math.round(inputTokens * 0.3);
        const outputChars = Math.round(outputTokens * 4);
        const cost = s.api_cost || (inputTokens * INPUT_COST_PER_TOKEN + outputTokens * OUTPUT_COST_PER_TOKEN);

        totalInputTokens += inputTokens;
        totalOutputTokens += outputTokens;
        totalInputChars += inputChars;
        totalOutputChars += outputChars;
        totalCost += cost;

        perLesson.push({
          id: s.id,
          date: s.lesson_timestamp,
          tutor: s.tutor_name,
          student: s.student_name,
          inputTokens,
          outputTokens,
          cost,
          estimated: !hasActual,
        });

        const day = s.lesson_timestamp ? new Date(s.lesson_timestamp).toLocaleDateString('en-CA') : 'unknown';
        if (!dailyMap[day]) dailyMap[day] = { date: day, sessions: 0, inputTokens: 0, outputTokens: 0, cost: 0, avgInputTokens: 0, avgOutputTokens: 0 };
        dailyMap[day].sessions++;
        dailyMap[day].inputTokens += inputTokens;
        dailyMap[day].outputTokens += outputTokens;
        dailyMap[day].cost += cost;
      }

      const daily = Object.values(dailyMap).sort((a, b) => a.date.localeCompare(b.date));
      for (const d of daily) {
        d.avgInputTokens = d.sessions > 0 ? Math.round(d.inputTokens / d.sessions) : 0;
        d.avgOutputTokens = d.sessions > 0 ? Math.round(d.outputTokens / d.sessions) : 0;
      }

      const avgCost = sess.length > 0 ? totalCost / sess.length : 0;
      const result = {
        sessions: sess.length, totalInputTokens, totalOutputTokens, totalCost, avgCost,
        totalInputChars, totalOutputChars,
        avgInputTokens: sess.length > 0 ? Math.round(totalInputTokens / sess.length) : 0,
        avgOutputTokens: sess.length > 0 ? Math.round(totalOutputTokens / sess.length) : 0,
        daily, perLesson,
      };

      setData(result);
      sessionStorage.setItem(cacheKey, JSON.stringify({ data: result, ts: Date.now() }));
    } catch (err) {
      console.error('Error loading stats:', err);
    }
    setLoading(false);
  };

  const cardClass = `rounded-lg border ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`;
  const formatTokens = (n) => n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(0)}K` : String(n);
  const formatCost = (n) => `$${n.toFixed(4)}`;
  const chartStyle = { background: isDark ? '#121212' : '#fff', border: `1px solid ${isDark ? '#27272a' : '#e5e5e5'}`, borderRadius: 8, fontSize: 12 };

  const tableTotalPages = data ? Math.ceil(data.perLesson.length / tablePerPage) : 0;
  const tableData = data ? data.perLesson.slice(tablePage * tablePerPage, (tablePage + 1) * tablePerPage) : [];

  return (
    <div className="space-y-6">
      <button onClick={() => navigate('/developer')} className={`flex items-center gap-2 text-sm ${isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666] hover:text-[#1a1a1a]'}`}>
        <ArrowLeft className="h-4 w-4" /> Back to Developer
      </button>

      <div className="flex items-center justify-between">
        <h1 className={`text-2xl font-semibold font-[Manrope] tracking-tight ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>API Statistics</h1>
        <div className="flex gap-1">
          {PERIODS.map(p => (
            <button key={p.label} onClick={() => { setPeriod(p.days); setTablePage(0); }}
              className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${period === p.days ? isDark ? 'bg-[#18181b] text-white' : 'bg-[#e8e8e8] text-[#1a1a1a]' : isDark ? 'text-[#52525b] hover:text-[#a1a1aa]' : 'text-[#999] hover:text-[#666]'}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className={`text-xs px-4 py-2 rounded-lg ${isDark ? 'bg-amber-400/5 text-amber-400/70 border border-amber-400/10' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
        Cost estimates based on Gemini 2.0 Flash pricing: $0.25/1M input, $2.00/1M output. Values marked * are estimated.
      </div>

      {loading ? (
        <div className="grid grid-cols-4 gap-3 animate-pulse">{[1,2,3,4].map(i => <div key={i} className={`${cardClass} h-20`} />)}</div>
      ) : data && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Lessons Processed', value: data.sessions, icon: FileText },
              { label: 'Total Cost', value: formatCost(data.totalCost), icon: DollarSign },
              { label: 'Avg Cost/Lesson', value: formatCost(data.avgCost), icon: DollarSign },
              { label: 'Total Tokens', value: formatTokens(data.totalInputTokens + data.totalOutputTokens), icon: Cpu },
            ].map(kpi => (
              <div key={kpi.label} className={`${cardClass} p-4`}>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{kpi.label}</span>
                  <kpi.icon className={`h-3.5 w-3.5 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`} strokeWidth={1.5} />
                </div>
                <div className={`text-xl font-semibold font-[Manrope] ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{kpi.value}</div>
              </div>
            ))}
          </div>

          {/* Input/Output breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={`${cardClass} p-5`}>
              <h3 className={`text-sm font-medium mb-1 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Input</h3>
              <div className={`text-2xl font-semibold font-[Manrope] ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{formatTokens(data.totalInputTokens)} tokens</div>
              <div className={`text-xs mt-1 space-y-0.5 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                <div>{data.totalInputChars.toLocaleString()} chars · {Math.round(data.totalInputChars / 4.5).toLocaleString()} words</div>
                <div>Avg {formatTokens(data.avgInputTokens)} tokens/lesson · ≈ {formatCost(data.totalInputTokens * INPUT_COST_PER_TOKEN)}</div>
              </div>
            </div>
            <div className={`${cardClass} p-5`}>
              <h3 className={`text-sm font-medium mb-1 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Output</h3>
              <div className={`text-2xl font-semibold font-[Manrope] ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{formatTokens(data.totalOutputTokens)} tokens</div>
              <div className={`text-xs mt-1 space-y-0.5 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                <div>Avg {formatTokens(data.avgOutputTokens)} tokens/lesson · ≈ {formatCost(data.totalOutputTokens * OUTPUT_COST_PER_TOKEN)}</div>
              </div>
            </div>
          </div>

          {/* Charts */}
          {data.daily.length > 1 && (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {[
                  { title: 'Sessions/Day', key: 'sessions', color: '#586f8d' },
                  { title: 'Daily Cost', key: 'cost', color: '#7662ac', format: v => `$${v.toFixed(4)}`, yFormat: v => `$${v.toFixed(2)}` },
                ].map(chart => (
                  <div key={chart.key} className={`${cardClass} p-5`}>
                    <h3 className={`text-sm font-medium mb-3 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{chart.title}</h3>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data.daily}>
                          <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#27272a' : '#e5e5e5'} />
                          <XAxis dataKey="date" tick={{ fontSize: 10, fill: isDark ? '#52525b' : '#999' }} tickFormatter={v => v.slice(5)} />
                          <YAxis tick={{ fontSize: 10, fill: isDark ? '#52525b' : '#999' }} tickFormatter={chart.yFormat} />
                          <Tooltip contentStyle={chartStyle} formatter={chart.format ? v => [chart.format(v), chart.title] : undefined} />
                          <Line type="monotone" dataKey={chart.key} stroke={chart.color} strokeWidth={1.5} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {[
                  { title: 'Input Tokens/Day', key: 'inputTokens', color: '#586f8d' },
                  { title: 'Output Tokens/Day', key: 'outputTokens', color: '#7662ac' },
                ].map(chart => (
                  <div key={chart.key} className={`${cardClass} p-5`}>
                    <h3 className={`text-sm font-medium mb-3 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{chart.title}</h3>
                    <div className="h-40">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data.daily}>
                          <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#27272a' : '#e5e5e5'} />
                          <XAxis dataKey="date" tick={{ fontSize: 10, fill: isDark ? '#52525b' : '#999' }} tickFormatter={v => v.slice(5)} />
                          <YAxis tick={{ fontSize: 10, fill: isDark ? '#52525b' : '#999' }} />
                          <Tooltip contentStyle={chartStyle} />
                          <Line type="monotone" dataKey={chart.key} stroke={chart.color} strokeWidth={1.5} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ))}
              </div>
              <div className={`${cardClass} p-5`}>
                <h3 className={`text-sm font-medium mb-3 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Avg Tokens/Lesson</h3>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data.daily}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#27272a' : '#e5e5e5'} />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: isDark ? '#52525b' : '#999' }} tickFormatter={v => v.slice(5)} />
                      <YAxis tick={{ fontSize: 10, fill: isDark ? '#52525b' : '#999' }} />
                      <Tooltip contentStyle={chartStyle} />
                      <Line type="monotone" dataKey="avgInputTokens" stroke="#586f8d" strokeWidth={1.5} dot={false} name="Input" />
                      <Line type="monotone" dataKey="avgOutputTokens" stroke="#7662ac" strokeWidth={1.5} dot={false} name="Output" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          )}

          {/* Per-Lesson Table */}
          <div className={cardClass}>
            <div className="p-5 pb-3">
              <h3 className={`text-sm font-medium ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Per-Lesson Breakdown</h3>
            </div>
            <div className={`divide-y ${isDark ? 'divide-[#27272a]' : 'divide-[#e5e5e5]'}`}>
              <div className={`grid grid-cols-7 gap-3 px-5 py-2 text-xs font-medium uppercase tracking-wider ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                <span>Date</span>
                <span>Tutor</span>
                <span>Student</span>
                <span>Input</span>
                <span>Output</span>
                <span>Total</span>
                <span>Cost</span>
              </div>
              {tableData.map(r => (
                <div key={r.id} className={`grid grid-cols-7 gap-3 px-5 py-2.5 items-center text-xs ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                  <span>{r.date ? new Date(r.date).toLocaleDateString([], { day: 'numeric', month: 'short' }) : '--'}</span>
                  <span className="truncate">{r.tutor || '--'}</span>
                  <span className="truncate">{r.student || '--'}</span>
                  <span>{formatTokens(r.inputTokens)}{r.estimated ? '*' : ''}</span>
                  <span>{formatTokens(r.outputTokens)}{r.estimated ? '*' : ''}</span>
                  <span>{formatTokens(r.inputTokens + r.outputTokens)}</span>
                  <span>{formatCost(r.cost)}</span>
                </div>
              ))}
            </div>
            {tableTotalPages > 1 && (
              <div className={`flex items-center justify-center gap-2 p-3 border-t ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
                <button onClick={() => setTablePage(Math.max(0, tablePage - 1))} disabled={tablePage === 0} className="p-1 disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
                <span className={`text-xs ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{tablePage + 1} / {tableTotalPages}</span>
                <button onClick={() => setTablePage(Math.min(tableTotalPages - 1, tablePage + 1))} disabled={tablePage >= tableTotalPages - 1} className="p-1 disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DeveloperStatistics;
